// ============================================================
// turnTrackerTurn - what happens when a turn ends and the next begins.
//
// The procedure behind useTurnTracker's End Turn, as plain functions
// over a context the hook builds each call. Not React, but not pure
// either: it writes through the hook's `updateTab` and `addNarrative`,
// and THE ORDER OF THOSE WRITES IS THE CONTRACT (pinned by
// useTurnTracker.trace.test.ts):
//
//   1. the outgoing participant's countdown ticks (turn END);
//   2. the turn advances; anyone it walked past ticks too;
//   3. on a round advance, every tick flag set this round is cleared;
//   4. the incoming participant's turn STARTS: prepared states expire,
//      burns are rolled, the tests that end them are rolled or queued,
//      then the burn lands and is reported;
//   5. the turn line; 6. a round that redraws the order rerolls.
//
// This runs only on the client that pressed End Turn, and every write
// reaches the others through the ordinary roster sync. Anything that
// ran per-client would apply a burn once per player.
// ============================================================

import { expireAtTurnStart } from '../engine/preparedStates';
import {
  advanceTurn,
  applyInitiative as applyInitiativePure,
  markCountdownTicked as markCountdownTickedPure,
  chargedActionPoints,
  type AdvanceTurnResult,
} from '../engine/turnTracker';
import type { InitiativeTieBreak } from '../engine/initiative';
import { computeCountdownPure } from '../engine/countdown';
import type { CountdownResult } from '../engine/countdown';
import { applyTurnEffectDamage, clearTurnEffect, getTurnEffects } from '../engine/turnEffects';
import type { TurnEffectRule } from '../engine/turnEffects';
import { getConditionTestModifier } from '../engine/conditionModifiers';
import type { ConditionModifier } from '../engine/conditionModifiers';
import type { UnnaturalDosMode } from '../engine/unnaturalDos';
import { rollCharacteristicTest, rollPlainTargetTest, buildTestNarrative } from '../engine/characteristicTests';
import { rollDiceExpression } from '../engine/dice';
import { rollAssignments } from './turnTrackerRolls';
import type { InitiativeSources } from './turnTrackerRolls';
import type { TurnTrackerState, TurnParticipant, Tab, Target, NarrativeEntry } from '../types';
import { pointsFor } from '../engine/actions/economy';

/**
 * One pending "roll to shake this off" test, handed to the UI.
 *
 * Local, not a Firestore document: unlike a defence prompt, nobody else
 * needs to resolve this, and a shared doc would be a second writer to
 * reason about.
 */
export interface TurnEffectTestRequest {
  combatantId: string;
  combatantName: string;
  rule: TurnEffectRule;
  /** The bearer's own modifiers to that characteristic, already resolved. */
  conditionMod: ConditionModifier;
}

type TabUpdater = (tab: Tab) => Tab;

/** The roster as this render saw it, and the two ways to write to it. */
export interface TurnWrites {
  tabs: Tab[];
  updateTab: (combatantId: string, updater: TabUpdater) => void;
  addNarrative: (combatantId: string, entry: NarrativeEntry) => void;
}

/** What the START of a turn reads: the writes, and how the tests that end an effect are rolled. */
interface TurnStartContext extends TurnWrites {
  autoResolveTurnEffects: boolean;
  resolveTurnEffectTest: (req: TurnEffectTestRequest, assist: number) => void;
  onTurnEffectTests?: (requests: TurnEffectTestRequest[]) => void;
  /**
   * A turn starts for this combatant (Squad Mode, 2026-10-23: his support range, a waiting entry, the free join he
   * opened, the one-shots he started). Told first, before the turn's effects; it writes through its own channel.
   */
  onTurnStart?: (combatantId: string) => void;
}

/**
 * What time passing reads (`runTimePasses`). It takes no test resolver: each
 * beat rolls its own, over the roster as THAT beat finds it.
 */
export interface TimePassesContext extends TurnWrites {
  protagonistRules: boolean;
  autoResolveTurnEffects: boolean;
  unnaturalDosMode: UnnaturalDosMode;
  onTurnEffectTests?: (requests: TurnEffectTestRequest[]) => void;
  onTurnStart?: (combatantId: string) => void;
}

/** Everything one End Turn reads. Built fresh by the hook for each press. */
export interface TurnEndContext extends TurnStartContext, InitiativeSources {
  state: TurnTrackerState;
  roundCountdown: boolean;
  protagonistRules: boolean;
  /** Whose turn the scan passes over - derived per render, never stored. */
  downedIds?: Set<string>;
  /** Reroll everyone when the round turns - the house rule AND auto-rolling both on. */
  rerollEachRound: boolean;
  initiativeTieBreak: InitiativeTieBreak;
}

const info = (text: string): NarrativeEntry => ({ type: 'info', text, timestamp: Date.now() });

const withTarget =
  (patch: (target: Target) => Partial<Target>): TabUpdater =>
  (t) => ({ ...t, target: { ...t.target, ...patch(t.target) } });

const clearTicked = withTarget(() => ({ countdownTickedThisRound: false }));

/** The display name a log line uses for a tab. */
const nameOf = (tab: Tab): string => tab.target.name || tab.label || '';

// ── The countdown (turn END) ─────────────────────────────────

/**
 * The countdown's result written onto the tab, regeneration included.
 * `markTicked` is the round's "already counted" flag: a turn sets it, a
 * beat of time outside the rounds (`runTimePasses`) does not.
 */
function countdownUpdater(cd: CountdownResult, markTicked = true): TabUpdater {
  return withTarget((target) => ({
    conditions: cd.newConds,
    wounds: cd.regenHealed > 0 ? { ...target.wounds, current: target.wounds.current + cd.regenHealed } : target.wounds,
    ...(markTicked ? { countdownTickedThisRound: true } : {}),
  }));
}

/**
 * Spend one combatant's turn on their timed conditions.
 *
 * Used for the outgoing participant AND for anybody whose turn was
 * skipped, because a skipped turn is still a turn going by. Returns the
 * id if it ticked, so the round-advance reset can account for it.
 */
function tickCountdownFor(ctx: TurnEndContext, combatantId: string): string | null {
  const tab = ctx.tabs.find((t) => t.id === combatantId);
  if (!tab || !ctx.roundCountdown || tab.target.countdownTickedThisRound) return null;
  const cd = computeCountdownPure(tab.target, nameOf(tab), ctx.roundCountdown, ctx.protagonistRules);
  ctx.updateTab(combatantId, cd ? countdownUpdater(cd) : withTarget(() => ({ countdownTickedThisRound: true })));
  for (const entry of cd?.narrativeEntries ?? []) ctx.addNarrative(combatantId, entry);
  return combatantId;
}

/** The outgoing participant ticks at the END of its turn - Stunned(3) is three full turns. */
function tickOutgoing(ctx: TurnEndContext, outgoing: TurnParticipant | undefined): string | null {
  if (!outgoing?.combatantId || outgoing.countdownTicked) return null;
  return tickCountdownFor(ctx, outgoing.combatantId);
}

/**
 * The ones the advance walked past. A combatant skipped for being down
 * still had a turn go by - without this a Dying(2) combatant was
 * skipped for being Dying, never ticked, and stayed Dying for the rest
 * of the fight (reported from a real session, 2026-08-19).
 */
function tickSkipped(ctx: TurnEndContext, skipped: readonly string[]): string[] {
  const ticked: string[] = [];
  for (const id of skipped) {
    const t = tickCountdownFor(ctx, id);
    if (t) ticked.push(t);
  }
  return ticked;
}

/**
 * Clear every tick flag this round set, on the tabs that carry one AND
 * on `alsoIds`.
 *
 * `alsoIds` IS THE WHOLE BUG it fixes. `tabs` is the snapshot the caller
 * closed over, so a combatant that ticked moments ago in the same call
 * still reads `false` there and would be skipped - its flag stayed true
 * into the new round, its countdown was refused, and every timed
 * condition on it ran at half speed. The ticked-now AND the ticked-
 * because-skipped both belong in it: leaving the second out kept the
 * identical bug alive for Dying, the common condition that makes you
 * skipped ("Dying takes two turns per point").
 */
export function clearTickFlags(
  writes: Pick<TurnWrites, 'tabs' | 'updateTab'>,
  alsoIds: ReadonlySet<string> = new Set(),
): void {
  for (const tab of writes.tabs) {
    if (tab.target.countdownTickedThisRound || alsoIds.has(tab.id)) writes.updateTab(tab.id, clearTicked);
  }
}

// ── The incoming turn (turn START) ───────────────────────────

/**
 * Prepared states die at their owner's turn start: the stance ends, and
 * a feint that never found its attack goes stale (v8.217.0). Aim
 * survives the boundary - see preparedStates.
 */
function expirePreparedStates(writes: TurnWrites, id: string, tab: Tab): void {
  const expired = expireAtTurnStart(tab.target.conditions ?? []);
  if (!expired.removed.length) return;
  writes.updateTab(
    id,
    withTarget((target) => ({ conditions: expireAtTurnStart(target.conditions ?? []).conds })),
  );
  writes.addNarrative(id, info(`⏱ ${expired.removed.join(' and ')} expired - the turn begins.`));
}

/** Whether a rule deals damage at all - dice or a flat amount. */
const dealsDamage = (rule: TurnEffectRule): boolean => Boolean(rule.damage) || rule.flatDamage != null;

/** The damage a rule rolls this turn. Consumes dice only for a dice rule. */
const rollBurn = (rule: TurnEffectRule): number =>
  rule.damage ? rollDiceExpression(rule.damage).total : (rule.flatDamage ?? 0);

/** Roll every damaging effect, in rule order, against the target: the total and one note each. */
function rollTurnBurns(effects: readonly TurnEffectRule[], target: Target): { total: number; notes: string[] } {
  let total = 0;
  const notes: string[] = [];
  for (const rule of effects.filter(dealsDamage)) {
    const result = applyTurnEffectDamage(rule, target, rollBurn(rule));
    total += result.damage;
    notes.push(result.note);
  }
  return { total, notes };
}

/**
 * The tests that end these effects, rolled through the pure engine rather
 * than useRollActions: that hook is bound to the attacker sheet and logs
 * to whichever tab is selected, the wrong log for a combatant whose turn
 * merely started. Rolled at once under the auto-resolve rule, otherwise
 * returned for the UI to prompt.
 */
function turnEffectTests(ctx: TurnStartContext, id: string, tab: Tab, effects: readonly TurnEffectRule[]) {
  const pending: TurnEffectTestRequest[] = [];
  for (const rule of effects) {
    if (!rule.endsOn) continue;
    // The combatant's OWN modifiers to that characteristic, routed through
    // conditionModifiers.ts as the parity rule requires - being Stunned
    // makes putting yourself out harder.
    const conditionMod = getConditionTestModifier(tab.target, { characteristic: rule.endsOn.charId });
    const request: TurnEffectTestRequest = { combatantId: id, combatantName: nameOf(tab), rule, conditionMod };
    if (ctx.autoResolveTurnEffects) ctx.resolveTurnEffectTest(request, 0);
    else pending.push(request);
  }
  return pending;
}

/**
 * HORDE-AWARE FIRE (ruling 2026-08-31): a crowd's health is MAGNITUDE,
 * not wounds - a burning horde used to burn nothing at all. The tick's
 * damage comes off the magnitude instead, and the fire dies with the horde.
 */
const burnMagnitude = (total: number): TabUpdater =>
  withTarget((target) => ({
    horde: { ...target.horde, magnitude: Math.max(0, (target.horde?.magnitude || 0) - total) },
  }));

/**
 * STOCK BEHAVIOUR, DELIBERATELY. Burning drives wounds negative and has no
 * death check of its own, so it can never kill on its own - it kills by
 * making the NEXT ordinary hit arrive as a severity-10 critical. Both paths
 * that turn a critical into a death are floored, so a protagonist is safe
 * without this site clamping anything, and clamping here would have made
 * him the one combatant who cannot go below zero.
 */
const burnWounds = (total: number): TabUpdater =>
  withTarget((target) => ({ wounds: { ...target.wounds, current: target.wounds.current - total } }));

/** Land the rolled burn on the tab, then report every note in its own log. */
function landBurn(writes: TurnWrites, id: string, tab: Tab, burn: { total: number; notes: string[] }): void {
  if (!burn.notes.length) return;
  const notes = [...burn.notes];
  if (burn.total > 0 && tab.target?.horde?.isHorde) {
    writes.updateTab(id, burnMagnitude(burn.total));
    notes.push(`👹 The crowd burns: ${burn.total} Magnitude lost to the flames.`);
  } else if (burn.total > 0) {
    writes.updateTab(id, burnWounds(burn.total));
  }
  for (const text of notes) writes.addNarrative(id, info(text));
}

/**
 * On Fire and its kin act at the START of a turn (rules-spec.md:534), the
 * other end of the turn from the countdown. Nothing starts while the
 * tracker is paused between turns. Returns the tests left for the UI.
 */
function startIncomingTurn(ctx: TurnEndContext, result: AdvanceTurnResult): TurnEffectTestRequest[] {
  const id = result.newActiveParticipant?.combatantId;
  if (!id || result.pausedBetweenTurns) return [];
  const tab = ctx.tabs.find((t) => t.id === id);
  return tab ? startTurnOf(ctx, tab) : [];
}

/** One combatant's turn START: prepared states expire, burns are rolled, their tests rolled or queued, the burn lands. */
function startTurnOf(ctx: TurnStartContext, tab: Tab): TurnEffectTestRequest[] {
  ctx.onTurnStart?.(tab.id);
  expirePreparedStates(ctx, tab.id, tab);
  const effects = getTurnEffects(tab.target.conditions, tab.target);
  // Burn dice are rolled BEFORE the tests' dice, and land after them.
  const burn = rollTurnBurns(effects, tab.target);
  const pending = turnEffectTests(ctx, tab.id, tab, effects);
  landBurn(ctx, tab.id, tab, burn);
  return pending;
}

// ── Announcing and redrawing ─────────────────────────────────

/** The line the outgoing combatant's log gets for this advance; empty for none. */
export function turnInfoText(result: AdvanceTurnResult): string {
  if (result.roundAdvanced) return `⚔ Round ${result.state.round} begins`;
  if (result.pausedBetweenTurns) return '⏸ Turn ended - waiting for GM to advance';
  return result.newActiveParticipant ? `⚔ ${result.newActiveParticipant.name}'s turn` : '';
}

/**
 * A table that redraws the order every round does it inside the same
 * snapshot as the advance, so one Ctrl+Z takes back the turn and the
 * reroll together. The sort may move somebody else to the front; the
 * round opens on whoever now leads it.
 */
function redrawForNewRound(ctx: TurnEndContext, outgoingId: string | null, result: AdvanceTurnResult) {
  if (!result.roundAdvanced || !ctx.rerollEachRound) return result.state;
  const sorted = applyInitiativePure(
    result.state,
    rollAssignments(result.state.participants, ctx),
    ctx.initiativeTieBreak,
  );
  const participants = sorted.participants.map((p, i) => ({
    ...p,
    status: i === 0 ? ('active' as const) : ('pending' as const),
  }));
  if (outgoingId) ctx.addNarrative(outgoingId, info('🎲 Initiative rerolled for the new round'));
  return { ...sorted, activeIndex: 0, participants };
}

/**
 * Run one End Turn's writes, in the contract order above, and return the
 * tracker state to commit. The caller owns the snapshot and the commit.
 */
export function runEndTurn(ctx: TurnEndContext): TurnTrackerState {
  const outgoing = ctx.state.participants[ctx.state.activeIndex];
  const outgoingId = outgoing?.combatantId ?? null;
  const tickedNow = tickOutgoing(ctx, outgoing);
  const result = advanceTurn(markCountdownTickedPure(ctx.state, outgoingId ?? ''), { downed: ctx.downedIds });
  const tickedSkipped = tickSkipped(ctx, result.skipped);
  if (result.roundAdvanced) clearTickFlags(ctx, new Set([tickedNow, ...tickedSkipped].filter((x): x is string => !!x)));
  const pending = startIncomingTurn(ctx, result);
  if (pending.length && ctx.onTurnEffectTests) ctx.onTurnEffectTests(pending);
  const turnInfo = turnInfoText(result);
  if (turnInfo && outgoingId) ctx.addNarrative(outgoingId, info(turnInfo));
  return chargeTurnStartTests(ctx, result, redrawForNewRound(ctx, outgoingId, result));
}

/**
 * A turn-start test that costs an action (Pinned's shake-off, a Full Action - 2026-10-26), rolled at once under
 * auto-resolve, is charged into the state this End Turn commits: a later write would read the state before it.
 * A prompted one is charged where its answer lands (useShellTurnTracker).
 */
function chargeTurnStartTests(ctx: TurnEndContext, result: AdvanceTurnResult, state: TurnTrackerState) {
  const id = result.newActiveParticipant?.combatantId;
  const tab = id && !result.pausedBetweenTurns && ctx.autoResolveTurnEffects ? ctx.tabs.find((t) => t.id === id) : null;
  if (!tab) return state;
  // What each turn-start test costs, off the action economy's own table (a Full Action is its two points).
  const points = getTurnEffects(tab.target.conditions, tab.target)
    .filter((r) => r.endsOn && r.actionCost)
    .reduce((sum, r) => sum + pointsFor(r.actionCost!), 0);
  return chargedActionPoints(state, tab.id, points);
}

// ── Time passing outside the rounds ──────────────────────────

/**
 * One combatant's timed conditions count down by one, whatever the round
 * countdown rule says and without the round's tick flag (there is no
 * round). Returns the tab AS IT NOW STANDS, for the half that follows.
 */
function countDownOf(ctx: TurnWrites & { protagonistRules: boolean }, tab: Tab): Tab {
  const cd = computeCountdownPure(tab.target, nameOf(tab), true, ctx.protagonistRules);
  if (!cd) return tab;
  const updater = countdownUpdater(cd, false);
  ctx.updateTab(tab.id, updater);
  for (const entry of cd.narrativeEntries) ctx.addNarrative(tab.id, entry);
  return updater(tab);
}

/**
 * One beat over `tabs`: for each combatant, in roster order, the timed
 * conditions count down, THEN the turn starts (prepared states expire,
 * what still burns burns, its test is rolled or queued).
 *
 * The countdown goes first because its write is absolute - it replaces the
 * conditions it computed from the tab it was handed - so it must read the
 * tab before anything else has touched it; the turn start then reads the
 * counted-down tab, and a fire that just went out does not burn once more.
 *
 * Every write is also applied to a copy kept here, and that copy is what
 * the beat returns: the NEXT beat must read the roster as this one left
 * it, and the caller's own roster has not caught up yet.
 */
function passOneBeat(ctx: TimePassesContext, tabs: Tab[]): { tabs: Tab[]; pending: TurnEffectTestRequest[] } {
  const after = new Map(tabs.map((t) => [t.id, t]));
  const updateTab: TurnWrites['updateTab'] = (id, updater) => {
    const was = after.get(id);
    if (was) after.set(id, updater(was));
    ctx.updateTab(id, updater);
  };
  const writes = { tabs, updateTab, addNarrative: ctx.addNarrative };
  const step = {
    ...writes,
    protagonistRules: ctx.protagonistRules,
    autoResolveTurnEffects: ctx.autoResolveTurnEffects,
    onTurnStart: ctx.onTurnStart,
    // Rolled against the roster as this beat has left it, and written through the same copy.
    resolveTurnEffectTest: (req: TurnEffectTestRequest, assist: number) =>
      resolveTurnEffectTestWith(
        { ...writes, tabs: [...after.values()], unnaturalDosMode: ctx.unnaturalDosMode },
        req,
        assist,
      ),
  };
  const pending: TurnEffectTestRequest[] = [];
  for (const tab of tabs) pending.push(...startTurnOf(step, countDownOf(step, tab)));
  return { tabs: tabs.map((t) => after.get(t.id) ?? t), pending };
}

/**
 * TIME PASSES: `beats` of it, for everyone on the roster at once - what
 * that many full rounds would have done to each, without anybody taking a
 * turn. See `passOneBeat` for one of them.
 *
 * Everybody is ticked, the downed included: a Dying combatant's count is
 * exactly what time passing is for. The tests a beat leaves for the UI are
 * handed over together at the end. The caller owns the snapshot (one undo
 * takes it all back) and the line in the log.
 */
export function runTimePasses(ctx: TimePassesContext, beats = 1): void {
  const pending: TurnEffectTestRequest[] = [];
  let tabs = ctx.tabs;
  for (let beat = 0; beat < beats; beat++) {
    // A turn start is told once per pass, on its first beat: its listener (Squad Mode) plans from the roster as this
    // render has it, which the later beats of one synchronous pass would only replay (the stage 6 review).
    const passed = passOneBeat(beat === 0 ? ctx : { ...ctx, onTurnStart: undefined }, tabs);
    tabs = passed.tabs;
    pending.push(...passed.pending);
  }
  if (pending.length && ctx.onTurnEffectTests) ctx.onTurnEffectTests(pending);
}

// ── The test that ends an effect ─────────────────────────────

/** Everything resolving one extinguish test reads. */
export interface TurnEffectTestContext extends TurnWrites {
  unnaturalDosMode: UnnaturalDosMode;
}

/**
 * The test's extra modifier: the bearer's condition penalties plus the
 * ally's Half Action, labelled so each shows as its own row.
 */
function testExtraMod(req: TurnEffectTestRequest, assist: number) {
  const mods = [...req.conditionMod.labels];
  if (assist) mods.push(`Ally assist +${assist}`);
  return { label: mods.join(', ') || 'Modifiers', value: req.conditionMod.total + assist };
}

/**
 * Roll one extinguish test and, on a success, clear the condition.
 *
 * `assist` is the ally's Half Action (+10, rules-spec.md:537). A
 * character sheet gives the raw characteristic; a statblock has none, so
 * it falls back to the plain-target test - the same approximation the
 * Flame weapon effect already makes.
 */
export function resolveTurnEffectTestWith(ctx: TurnEffectTestContext, req: TurnEffectTestRequest, assist: number) {
  const tab = ctx.tabs.find((t) => t.id === req.combatantId);
  const endsOn = req.rule.endsOn;
  if (!tab || !endsOn) return;
  const opts = { mode: ctx.unnaturalDosMode, extraMod: testExtraMod(req, assist) };
  const result = tab.sourceCharacter
    ? rollCharacteristicTest(tab.sourceCharacter, endsOn.charId, 0, 0, opts)
    : rollPlainTargetTest(tab.target, endsOn.plainTargetTest, 0, 0, opts);
  const structured = buildTestNarrative(result, req.combatantName);
  // A test that costs his Full Action says so (Pinned's shake-off, 2026-10-26).
  const text = req.rule.actionCost === 'full' ? `${structured.summary} - his Full Action.` : structured.summary;
  ctx.addNarrative(req.combatantId, {
    type: 'test-result',
    text,
    structured: { ...structured, summary: text },
    timestamp: Date.now(),
  });
  if (!result.success) return;
  ctx.updateTab(
    req.combatantId,
    withTarget((target) => ({ conditions: clearTurnEffect(target.conditions || [], req.rule) })),
  );
  ctx.addNarrative(req.combatantId, info(`✅ ${req.rule.label} ${req.rule.clearVerb ?? 'extinguished'}.`));
}
